import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtomValue, useSetAtom } from "jotai";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ADMIN_BASE, tenantPath } from "../lib/paths.js";
import { apiCall } from "../lib/api-client.js";
import {
  ApiError,
  type LoginResponse,
} from "../lib/api-types.js";
import { authAtom, loginAtom } from "../lib/auth-store.js";
import { Spinner } from "../components/Spinner.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { LanguageSwitcher } from "../components/LanguageSwitcher.js";

/**
 * 平台超管登录（2026-07-04 入口分离）：本页只服务 super/support。
 * 企业管理员走自己的入口 `/:orgCode`（TenantLayout 就地登录），两入口互不出现对方形态。
 */
export function Login(): JSX.Element {
  const { t } = useTranslation();
  const auth = useAtomValue(authAtom);
  const setLogin = useSetAtom(loginAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectTo =
    (location.state as { from?: string } | null)?.from ?? ADMIN_BASE;

  // 已登录（含硬加载深链时 atomWithStorage hydrate 完成的那一帧）→ 回原路径，
  // 不硬编码 "/"（否则深链一律被冲回首页）。org-admin 误入平台入口 → 回自己企业后台。
  if (auth) {
    if (auth.scope === "org-admin" && auth.orgCode) {
      return <Navigate to={tenantPath(auth.orgCode)} replace />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiCall<LoginResponse>("POST", "/api/admin/login", {
        body: { user, password },
      });
      setLogin({
        token: res.token,
        scope: res.scope,
        expiresAt: res.expiresAt,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError(t("admin.login.error.invalidCredentials"));
        } else if (err.status === 400) {
          setError(t("admin.login.error.missingFields"));
        } else {
          setError(err.message || t("admin.login.error.generic", { code: err.code }));
        }
      } else {
        setError(t("admin.login.error.network"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded border border-dosia-border bg-dosia-bg-input px-3 py-1.5 text-sm text-dosia-text focus:border-dosia-accent focus:outline-none";

  return (
    <div className="min-h-screen flex items-center justify-center bg-dosia-bg px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-dosia-border bg-dosia-bg-light p-6 shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-dosia-text">
              {t("admin.login.title")}
            </h1>
            <p className="text-sm text-dosia-text-secondary">
              {t("admin.login.subtitle")}
            </p>
          </div>
          <LanguageSwitcher />
        </div>

        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

        <label className="block space-y-1">
          <span className="text-sm text-dosia-text-secondary">
            {t("admin.login.field.username")}
          </span>
          <input
            type="text"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            autoComplete="username"
            autoFocus
            required
            className={inputClass}
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm text-dosia-text-secondary">
            {t("admin.login.field.password")}
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className={inputClass}
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 rounded bg-dosia-accent px-4 py-2 text-sm font-medium text-dosia-bg-light hover:bg-dosia-accent-hover disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? <Spinner size="sm" className="text-dosia-bg-light" /> : null}
          {submitting ? t("admin.login.submitting") : t("admin.login.submit")}
        </button>

      </form>
    </div>
  );
}
